import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import analytics from "@/utils/analytics";
import { loadMarketingScripts, withdrawMarketingTracking } from "@/utils/marketingScripts";

const COOKIE_NAME = "cando_cookie_consent";
// Footer "Cookie Settings" dispatches this to re-open the preference centre.
export const OPEN_PREFS_EVENT = "cando:open-cookie-preferences";

function getRawCookie(name) {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? match[2] : null;
}

// Returns the parsed consent object, or null if absent / malformed / the
// pre-launch legacy "accepted|declined" string format. Null → no consent
// recorded → re-show the banner (the safe direction; no migration needed
// as there are no production users yet).
function readConsent() {
  const raw = getRawCookie(COOKIE_NAME);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(raw));
    if (
      parsed &&
      parsed.strictly_necessary === true &&
      typeof parsed.analytics === "boolean"
    ) {
      return parsed;
    }
  } catch {
    /* legacy/invalid value — fall through to null */
  }
  return null;
}

function writeConsent(analyticsGranted) {
  const payload = {
    strictly_necessary: true,
    analytics: analyticsGranted,
    timestamp: new Date().toISOString(),
  };
  const value = encodeURIComponent(JSON.stringify(payload));
  const expires = new Date(Date.now() + 365 * 864e5).toUTCString();
  // Secure only over HTTPS so localhost dev (http) still persists.
  const secure = window.location.protocol === "https:" ? ";Secure" : "";
  document.cookie = `${COOKIE_NAME}=${value};expires=${expires};path=/;SameSite=Lax${secure}`;
}

function Toggle({ checked, disabled, onChange, label, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={disabled ? undefined : () => onChange(!checked)}
      data-testid={testId}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${
        checked ? "bg-brand-aqua" : "bg-gray-300"
      } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-0.5 ${
          checked ? "translate-x-[22px]" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

export default function CookieConsent() {
  // "hidden" | "banner" | "prefs"
  const [view, setView] = useState("hidden");
  const [analyticsOn, setAnalyticsOn] = useState(false);

  // On mount: apply a stored choice, or schedule the first-visit banner.
  useEffect(() => {
    const consent = readConsent();
    if (consent) {
      analytics.consentUpdate({ analytics: consent.analytics });
      // Returning visitor with prior consent — load tracking/marketing
      // scripts (SalesIQ + PageSense) without waiting for another click.
      if (consent.analytics) loadMarketingScripts();
      return undefined;
    }
    // Don't schedule the banner during react-snap prerendering: the crawl
    // outlasts the 1.5s timer, baking the banner into the snapshot HTML while
    // the client's first hydration render is "hidden" — a guaranteed React
    // #418 text mismatch on every prerendered page. Real visitors are never
    // ReactSnap, so behaviour is unchanged for them.
    if (navigator.userAgent.includes("ReactSnap")) return undefined;
    const timer = setTimeout(() => setView("banner"), 1500);
    return () => clearTimeout(timer);
  }, []);

  // Footer "Cookie Settings" → reopen the preference centre, pre-filled
  // with the current stored choice (defaults off if none recorded).
  useEffect(() => {
    const openPrefs = () => {
      const consent = readConsent();
      setAnalyticsOn(consent ? consent.analytics : false);
      setView("prefs");
    };
    window.addEventListener(OPEN_PREFS_EVENT, openPrefs);
    return () => window.removeEventListener(OPEN_PREFS_EVENT, openPrefs);
  }, []);

  const persist = useCallback((analyticsGranted) => {
    writeConsent(analyticsGranted);
    analytics.consentUpdate({ analytics: analyticsGranted });
    // Same gate as the on-mount returning-visitor branch — fire the
    // Zoho marketing-script loader the moment the user grants analytics.
    // Idempotent, so multiple grants in one session don't double-load.
    if (analyticsGranted) loadMarketingScripts();
    else withdrawMarketingTracking();
    setView("hidden");
  }, []);

  const acceptAll = useCallback(() => persist(true), [persist]);
  const rejectAll = useCallback(() => persist(false), [persist]);
  const savePreferences = useCallback(
    () => persist(analyticsOn),
    [persist, analyticsOn]
  );

  const openPreferences = useCallback(() => {
    const consent = readConsent();
    setAnalyticsOn(consent ? consent.analytics : false);
    setView("prefs");
  }, []);

  // Closing the centre must not silently grant/deny. If a choice already
  // exists, just close; if not (first visit), fall back to the banner so
  // an explicit choice is still required.
  const closePreferences = useCallback(() => {
    setView(readConsent() ? "hidden" : "banner");
  }, []);

  if (view === "hidden") return null;

  if (view === "prefs") {
    return (
      <div
        className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-brand-black/50"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-prefs-title"
        data-testid="cookie-prefs"
      >
        <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl p-6 sm:p-7 max-h-[90vh] overflow-y-auto">
          <div className="flex items-start justify-between gap-4">
            <h2
              id="cookie-prefs-title"
              className="font-heading text-lg font-bold text-brand-black"
            >
              Cookie Preferences
            </h2>
            <button
              onClick={closePreferences}
              className="text-brand-grey hover:text-brand-black p-1"
              aria-label="Close cookie preferences"
              data-testid="cookie-prefs-close"
            >
              <X size={18} />
            </button>
          </div>
          <p className="text-xs text-brand-grey leading-relaxed mt-2">
            {"Choose which cookies we can use. Read more in our "}
            <Link
              to="/cookie-policy"
              className="underline hover:text-brand-black"
              onClick={() => setView("hidden")}
            >
              Cookie Policy
            </Link>
            .
          </p>

          <div className="mt-5 space-y-4">
            <div className="flex items-start justify-between gap-4 border border-brand-light-grey rounded-xl p-4">
              <div>
                <p className="text-sm font-semibold text-brand-black">
                  Strictly Necessary
                </p>
                <p className="text-xs text-brand-grey leading-relaxed mt-1">
                  Required for the site to function (e.g. remembering this
                  consent choice). These cannot be switched off.
                </p>
              </div>
              <Toggle
                checked
                disabled
                label="Strictly necessary cookies (always on)"
                testId="cookie-toggle-necessary"
              />
            </div>

            <div className="flex items-start justify-between gap-4 border border-brand-light-grey rounded-xl p-4">
              <div>
                <p className="text-sm font-semibold text-brand-black">
                  Analytics
                </p>
                <p className="text-xs text-brand-grey leading-relaxed mt-1">
                  Google Analytics, so we can understand how the site is used
                  and improve it. Off by default.
                </p>
              </div>
              <Toggle
                checked={analyticsOn}
                onChange={setAnalyticsOn}
                label="Analytics cookies"
                testId="cookie-toggle-analytics"
              />
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-2">
            <button
              onClick={savePreferences}
              className="flex-1 bg-brand-aqua hover:bg-brand-aqua-dark text-white text-xs font-semibold rounded-full px-5 py-2.5 transition-colors"
              data-testid="cookie-prefs-save"
            >
              Save Preferences
            </button>
            <button
              onClick={acceptAll}
              className="flex-1 border border-brand-aqua text-brand-aqua hover:bg-brand-aqua-light text-xs font-semibold rounded-full px-5 py-2.5 transition-colors"
              data-testid="cookie-prefs-accept"
            >
              Accept All
            </button>
            <button
              onClick={rejectAll}
              className="flex-1 border border-brand-aqua text-brand-aqua hover:bg-brand-aqua-light text-xs font-semibold rounded-full px-5 py-2.5 transition-colors"
              data-testid="cookie-prefs-reject"
            >
              Reject All
            </button>
          </div>
        </div>
      </div>
    );
  }

  // view === "banner"
  return (
    <div
      className="fixed top-0 left-0 right-0 md:top-auto md:bottom-24 md:right-auto md:left-6 md:max-w-md z-[60] bg-white border-b md:border border-brand-light-grey md:rounded-2xl p-4 md:p-5 shadow-xl animate-slide-up"
      data-testid="cookie-consent"
      role="region"
      aria-label="Cookie consent"
    >
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <p className="text-xs text-brand-grey leading-relaxed">
            {"We use strictly necessary cookies to run this site, and optional analytics cookies (off until you allow them) to improve it. Learn more in our "}
            <Link
              to="/cookie-policy"
              className="underline hover:text-brand-black"
              onClick={() => setView("hidden")}
            >
              Cookie Policy
            </Link>
            .
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <button
              onClick={acceptAll}
              className="bg-brand-aqua hover:bg-brand-aqua-dark text-white text-xs font-semibold rounded-full px-5 py-2 transition-colors"
              data-testid="cookie-accept"
            >
              Accept All
            </button>
            <button
              onClick={rejectAll}
              className="border border-brand-aqua text-brand-aqua hover:bg-brand-aqua-light text-xs font-semibold rounded-full px-5 py-2 transition-colors"
              data-testid="cookie-decline"
            >
              Reject All
            </button>
            <button
              onClick={openPreferences}
              className="text-brand-grey hover:text-brand-black text-xs font-medium underline px-2 py-2 transition-colors"
              data-testid="cookie-manage"
            >
              Manage Preferences
            </button>
          </div>
        </div>
        <button
          onClick={rejectAll}
          className="text-brand-grey hover:text-brand-black p-1"
          aria-label="Reject all and close"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
